import { useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'
import { MouseEventHandler, useEffect, useState } from 'react'

import { BlockContent, Link } from '~/components'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { QUIZ_SUBJECT_RATING, RATING_ITEMS } from '~/types/homebaseQuiz.types'
import { useSetMappingQuizAnswerMutation } from '~/curriculum/apis/mapping-quiz.api'
import { selectUserId } from '~/auth/slices/auth.slice'
import { EventDetectionMethod, EventType } from '~/behavior-tracking/types'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'
import { extractCountryCodeAndGradeIdFromRouterPath } from '~/curriculum/utils/extractQueryParams'
import {
  selectCurriculumSubjectContent,
  selectShouldResetCurriculumMappingQuiz,
  setShouldResetCurriculumMappingQuizActionCreator,
} from '~/curriculum/slice/curriculum.slice'
import useGetMappingQuiz from '~/curriculum/hooks/useGetMappingQuiz'
import {
  CurriculumMappingQuizQuestionResponseDto,
  CurriculumMappingQuizTask,
} from '~/curriculum/types/mapping-quiz.types'
import HouseOfMathLoader from '~/components/HouseOfMathLoader'
import blockContentStyle from '~/shared/styles/BootcampSanityBlock.module.css'
import {
  addActiveCompetency,
  clearAndUpdateActiveCompetencyList,
  setScrollItemIdOnTopicPage,
} from '~/curriculum/slice/persistCurriculum.slice'
import { getStepTwoExpandedKey } from '~/curriculum/utils/helpers'
import { selectStepTwoExpanded, setStepTwoExpanded } from '~/redux/slices/dashboard'

import MindButton from '~/onboarding/components/buttons/MindButton'

import { Breakpoint, useCheckBreakpoint } from '~/utils'

import CurriculumMappingQuizRatingItem from '../components/Quiz/CurriculumMappingQuizRatingItem'

import MappingQuizQuestionCompletionPage from './MappingQuizCompletion.page'

export default function MappingQuizQuestionPage(): React.ReactElement {
  const { t } = useTranslation(['onboarding', 'curriculum'])
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  const router = useRouter()
  const dispatch = useAppDispatch()
  const userId = useAppSelector(selectUserId)
  const expandedItems = useAppSelector(selectStepTwoExpanded)

  const [currentQuestion, setCurrentQuestion] = useState<CurriculumMappingQuizQuestionResponseDto | null>(null)
  const [tasks, setTasks] = useState<CurriculumMappingQuizTask[] | null>(null)
  const [firstQuestionLoaded, setFirstQuestionLoaded] = useState(false)

  const [animation, setAnimation] = useState<'slide-out' | 'slide-in' | null>(null)

  const { countryCode, gradeLevel, subjectOrder } = extractCountryCodeAndGradeIdFromRouterPath(router)

  const { subjectLabel, competencyGoals } = useAppSelector(selectCurriculumSubjectContent)
  const shouldResetMQ = useAppSelector(selectShouldResetCurriculumMappingQuiz)

  const [setMappingQuizAnswer, { isLoading }] = useSetMappingQuizAnswerMutation()
  const subjectUrlPage = router.asPath.replace('mapping-quiz', '')

  const [mindUpdated, setMindUpdated] = useState(false)

  const { mappingQuizQuestion, isLoading: quizLoading } = useGetMappingQuiz({
    countryCode,
    gradeLevel,
    subjectOrder,
    subjectLabel,
    version: 'v1',
    reset: shouldResetMQ,
    skip: firstQuestionLoaded || !subjectLabel || !subjectOrder,
  })

  useEffect(() => {
    const totalTask = tasks?.reduce((total, item) => total + item.subjectList.length, 0) || 0
    if (tasks && tasks.length > 0) {
      const firstTask = tasks[0]
      const firstSubjectList = firstTask.subjectList?.[0]
      const subjectId = firstSubjectList.levels[0].levelIds[0]
      const topicId = `${firstTask.subject}-${firstSubjectList.slug}-${subjectId}`
      dispatch(clearAndUpdateActiveCompetencyList(subjectId))
      dispatch(addActiveCompetency(topicId))
      dispatch(setScrollItemIdOnTopicPage(subjectId))
    } else if (totalTask === 0) {
      const stepTwoExpandedKey = getStepTwoExpandedKey(countryCode, Number(gradeLevel), subjectOrder)
      if (!expandedItems?.includes(stepTwoExpandedKey)) {
        dispatch(setStepTwoExpanded([...(expandedItems || []), stepTwoExpandedKey]))
      }
    }
  }, [countryCode, dispatch, expandedItems, gradeLevel, subjectOrder, tasks])

  // set current question when quiz question is fetched
  useEffect(() => {
    if (mappingQuizQuestion) {
      setFirstQuestionLoaded(true)
      setCurrentQuestion(mappingQuizQuestion)
      setTimeout(() => {
        dispatch(setShouldResetCurriculumMappingQuizActionCreator(false))
      }, 500)
    }
  }, [dispatch, mappingQuizQuestion])

  const quizId = currentQuestion?.quizId
  const questionId = currentQuestion?.questionId

  const logClickEvent = async (answer: number, isQuizComplete: boolean = false): Promise<void> => {
    if (userId && currentQuestion) {
      const extra = {
        title: isQuizComplete ? 'Complete Mapping Quiz' : currentQuestion.title,
        countryCode,
        gradeLevel,
        subjectOrder,
        answer,
        quarterNo: competencyGoals[0]?.subdivisionOrder,
      }
      const event = {
        eventName: EventType.CURRICULUM_EVENT,
        viewKey: router.asPath,
        extra: {
          detectBy: isQuizComplete
            ? EventDetectionMethod.ON_MAPPING_QUIZ_COMPLETE_CLICK
            : EventDetectionMethod.ON_MAPPING_QUIZ_ANSWER_CLICK,
          ...extra,
        },
      }
      await logUserActivity(userId, event)
    }
  }
  const handleNextQuestion =
    (ratingValue: number): MouseEventHandler<HTMLDivElement> =>
    async (event) => {
      event.preventDefault()
      if (quizId && questionId && !isLoading && !quizLoading) {
        await new Promise((resolve) => setTimeout(resolve, 300))
        setAnimation('slide-out')

        setMindUpdated(true)

        logClickEvent(ratingValue)
        const response = await setMappingQuizAnswer({
          countryCode,
          gradeLevel,
          curriculumSubject: subjectLabel,
          preAskedQuesIds: currentQuestion.preAskedQuesIds ?? [],
          questionId,
          quizId: currentQuestion.quizId,
          subjectOrder,
          answer: ratingValue,
          version: 'v1',
        })
        if ('data' in response) {
          const { data: nextQuestion } = response
          const isQuizComplete = nextQuestion?.isQuizComplete
          if (isQuizComplete && nextQuestion?.tasks?.length) {
            setTasks(nextQuestion.tasks)
          }
          setCurrentQuestion(nextQuestion)
          setAnimation('slide-in')
        }
      }
    }

  if (currentQuestion?.isQuizComplete) {
    return (
      <MappingQuizQuestionCompletionPage
        onMappingQuizCompletion={() => {
          logClickEvent(0, true)
        }}
      />
    )
  }

  const mindButton = (): React.ReactElement => {
    return (
      <MindButton
        className="top-6 md:-top-21 lg:-top-18 right-4 md:-right-2 absolute w-full"
        imageClassName="w-24"
        mindUpdated={mindUpdated}
        setMindUpdated={setMindUpdated}
        tosterMessage={t('curriculum:mindGotUpdated')}
      />
    )
  }

  return (
    <div className="bg-gradient-purple-dark">
      <section className="flex flex-col justify-center items-center rounded w-full min-h-screen overflow-x-hidden overflow-y-auto">
        {isMobile && mindButton()}
        <div className="flex flex-col justify-center items-center mt-2 md:mt-8 xl:mt-28">
          <p className="flex justify-center -mt-4 mb-4 max-w-72 md:max-w-md lg:max-w-3xl font-bold text-white md:text-5 text-xs text-center">
            {t('mappingYourMustKnows', { topic: subjectLabel })}
          </p>
          <div className="static flex justify-center items-center gap-4 bg-white bg-gradient-to-r from-orange via-pink-logo to-blue-logo drop-shadow-white p-0.5 rounded w-80 md:w-165 lg:w-216 h-60 md:h-96 xl:h-105 transform-gpu">
            <div className="md:relative flex flex-col items-center gap-4 lg:gap-8 bg-white px-4 md:px-8 py-2 rounded-2.25 w-full h-full">
              {!currentQuestion && <HouseOfMathLoader />}
              {!isMobile && mindButton()}
              {currentQuestion && currentQuestion.title && (
                <div
                  className={`${
                    animation === 'slide-out'
                      ? 'animate-slideOutLeft'
                      : animation === 'slide-in'
                      ? 'animate-slideInRight'
                      : ''
                  } w-full`}
                >
                  <div className="flex justify-center items-center mb-2 md:my-2">
                    <h4 className="font-semibold text-black-light xl:text-h3 text-sm md:text-lg text-center">
                      {currentQuestion.title}
                    </h4>
                  </div>
                  <div className="flex justify-center items-center gap-4 bg-task-algebra-gradient drop-shadow-white p-0.5 rounded w-full h-34 md:h-68">
                    <div className="flex flex-col justify-center items-center gap-4 bg-white py-1 md:py-6 rounded-2.25 w-full h-full">
                      <div
                        className={`${blockContentStyle['curriculum-block-content']} flex flex-col justify-center h-full`}
                      >
                        {currentQuestion && (
                          <BlockContent
                            blocks={currentQuestion.description}
                            className="flex justify-center items-center w-full h-full text-black-light text-center leading-16"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-center items-center gap-2 w-80 md:w-165 lg:w-216 ">
          {RATING_ITEMS.map(({ emoji, rating, isSmallEmoji }) => (
            <CurriculumMappingQuizRatingItem
              emoji={emoji}
              handleOnClick={handleNextQuestion}
              isLoading={isLoading || quizLoading}
              isSmallEmoji={isSmallEmoji}
              key={rating}
              ratingValue={rating as QUIZ_SUBJECT_RATING}
            />
          ))}
        </div>
        <div className="mt-10 py-6">
          <Link className="text-white hover:text-gray-200 text-sm hover:underline" to={subjectUrlPage}>
            Skip
          </Link>
        </div>
      </section>
    </div>
  )
}
